import _ from 'lodash';
import Moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { Box, Card as MuiCard } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useSelector } from 'react-redux';
import withRouter from '../../../components/hocs/withRouter';
import { useDelay } from '../../../components/hooks/useDelay';
import { QuoteStepsP250, QuoteStepsP100 } from './QuoteSteps';
import { QuoteStepLoader } from './QuoteStepLoader';
import { GeneratingPolicyLoader } from './GeneratingPolicyLoader';

import {
  fetchP250QuotePageDetails,
  fetchQuoteDetails,
} from '../../../inbox/QuotesService';
import { getPolicyStatus } from '../_services/dashboard.service';

import store from '../../../config/storeConfig';
import { ProductTypes } from '../../../types';
import useNotifications from '../../../_lib/notifications/useNotifications';

// Related Jira - FIR-1746

export const SignQuote = withRouter(({ history, location }: any) => {
  const classes = useStyles();
  const [done, setDone] = useState(false);
  // @ts-expect-error TS(2339): Property 'quoteId' does not exist on type '{}'.
  const { quoteId, product } = useParams();
  const accountId = useSelector(({ account }) => account.id);
  const processor = useSelector(({ account }) => account.paymentProcessor);

  const {
    loading,
    data: { quote, existingPolicy },
  } = useGetDependencies(quoteId, accountId, product);

  const policy = usePolicyPoller(
    // @ts-expect-error TS(2339): Property 'id' does not exist on type '{}'.
    quote.id,
    product,
    setDone,
    history,
    // @ts-expect-error TS(2339): Property 'agencyBilled' does not exist on type '{}... Remove this comment to see the full error message
    quote.agencyBilled
  );

  const [showPreparingDocsHelperText, setShowPreparingDocsHelperText] =
    useState(true);
  const hidePreparingDocsHelperText = React.useCallback(() => {
    setShowPreparingDocsHelperText(false);
  }, []);

  const isPreparingDocsView =
    !loading && !_.isEmpty(quote) && product !== ProductTypes.p100;

  useDelay(isPreparingDocsView ? 10000 : null, hidePreparingDocsHelperText);

  if (loading || _.isEmpty(quote)) {
    return <QuoteStepLoader />;
  }

  if (product === ProductTypes.p100) {
    return (
      <GeneratingPolicyLoader
        policy={policy}
        quote={quote}
        history={history}
        // @ts-expect-error TS(2339): Property 'customerStatus' does not exist on type '... Remove this comment to see the full error message
        status={quote.customerStatus}
        setDone={setDone}
        product={product}
        // @ts-expect-error TS(2339): Property 'isEndorsement' does not exist on type '{... Remove this comment to see the full error message
        isEndorsement={quote.isEndorsement}
      />
    );
  }

  // @ts-expect-error TS(2339): Property 'customerStatus' does not exist on type '... Remove this comment to see the full error message
  if (quote.customerStatus === 'SIGNED' && !_.isEmpty(existingPolicy)) {
    // @ts-expect-error TS(2339): Property 'agencyBilled' does not exist on type '{}... Remove this comment to see the full error message
    if (quote.agencyBilled) {
      return <Redirect to="/customer/policies" />;
    }

    return (
      <Redirect
        to={`/customer/policies/${existingPolicy.id}/pay/${processor}/${product}`}
      />
    );
  }

  return (
    <>
      {product === ProductTypes.p100 && (
        // @ts-expect-error TS(2339): Property 'agencyBilled' does not exist on type '{}... Remove this comment to see the full error message
        <QuoteStepsP100 location={location} agencyBilled={quote.agencyBilled} />
      )}
      {product === ProductTypes.p250 && (
        // @ts-expect-error TS(2339): Property 'agencyBilled' does not exist on type '{}... Remove this comment to see the full error message
        <QuoteStepsP250 location={location} agencyBilled={quote.agencyBilled} />
      )}
      <section style={{ height: '100vh' }}>
        <MuiCard style={{ height: '100%' }}>
          <Box p={3} className={classes.container}>
            <Box
              p={3}
              className={classes.textContainer}
              style={
                showPreparingDocsHelperText
                  ? { justifyContent: 'space-between' }
                  : { justifyContent: 'flex-end' }
              }
            >
              {showPreparingDocsHelperText && (
                <div className={classes.helperText}>
                  Please wait while we are preparing the documents for you to
                  sign. This may take a few seconds.
                </div>
              )}
            </Box>
            <iframe
              className={classes.iframe}
              // @ts-expect-error TS(2339): Property 'signUrl' does not exist on type '{}'.
              src={quote.signUrl}
              title="buy"
              allowFullScreen
              // @ts-expect-error TS(2322): Type '{ className: string; src: any; title: string... Remove this comment to see the full error message
              webkitallowfullscreen
              mozallowfullscreen
            />
          </Box>
        </MuiCard>
      </section>
    </>
  );
});

const useStyles = makeStyles(({ palette, config }) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '.75rem',
  },
  textContainer: {
    display: 'flex',
    padding: '.2rem 0',
  },
  iframe: {
    height: '100%',
    left: 0,
    top: 0,
    width: '100%',
    border: 0,
    background: palette.background.iframe,
  },
  helperText: {
    fontSize: config.textSizes.primer,
    paddingBottom: '0.1rem',
  },
}));

const usePolicyPoller = (
  quoteId: any,
  product: any,
  setDone: any,
  history: any,
  agencyBilled: any
) => {
  const notifications = useNotifications();
  const [policy, setPolicy] = React.useState({});
  const pollExpiration = React.useRef(Moment().add(20, 'minutes').unix());
  React.useEffect(() => {
    if (quoteId && product) {
      const poller = setInterval(() => {
        getPolicyStatus({ quoteId, product })
          // Check the failure conditions, no policy data or passed expiration
          .then(({ data }: any) => {
            // FIR-1746
            if (!data && Moment().unix() >= pollExpiration.current) {
              notifications.error(
                'Sign Quote Timeout. Please start over again.'
              );
              history.push('/customer/policies');
              pollExpiration.current = null;
              return;
            }
            if (!data.status) {
              // skip the execution of the next block
              return Promise.reject();
            }
            return data;
          })
          // handle valid statuses
          .then((data: any) => {
            pollExpiration.current = null;
            if (data) {
              setPolicy(data);
              if (product === ProductTypes.p250) {
                const status = _.get(data, 'status');
                const policyId = _.get(data, 'id');
                const isPaid = _.get(data, 'isPaid');
                // @ts-expect-error TS(2339): Property 'account' does not exist on type '{}'.
                const { paymentProcessor } = store.getState().account;

                if (status === 'REQUESTED') {
                  notifications.warning(
                    "Looks like you did everything right but it's taking longer than expected to generate your policy. We'll drop you off somewhere safe and notify you when we've got it!"
                  );

                  setTimeout(() => {
                    setDone(false);
                    history.push(`/customer/policies`);
                  }, 5000);
                }

                if (status === 'INVALID') {
                  notifications.error(
                    "Looks like something went wrong while generating your policy. We'll drop you off somewhere safe to try again."
                  );

                  setTimeout(() => {
                    setDone(false);
                    history.push(`/customer/policies`);
                  }, 5000);
                }

                if (!status) {
                  notifications.error(
                    "Hmm something doesn't look right... We're sending you back a step just in case you missed something"
                  );

                  setTimeout(() => {
                    setDone(false);
                    history.push(`/customer/quotes/${quoteId}/sign/${product}`);
                  }, 5000);
                }

                if (['ACTIVE', 'ISSUED', 'IN FORCE'].includes(status)) {
                  notifications.success('Policy Signed Sucessfully!');

                  if (agencyBilled || isPaid) {
                    setTimeout(() => {
                      setDone(false);
                      clearInterval(poller);
                      history.push(`/customer/policies/`);
                    }, 2000);
                  } else {
                    setTimeout(() => {
                      setDone(false);
                      clearInterval(poller);
                      history.push(
                        `/customer/policies/${policyId}/pay/${paymentProcessor}/${product}`
                      );
                    }, 2000);
                  }
                }
              }
            }
          })
          .catch(console.error.bind(console));
        // check every 30 seconds
      }, 5000);
      return () => {
        clearInterval(poller);
      };
    }
  }, [agencyBilled, notifications, history, product, quoteId, setDone]);
  return policy;
};

const useGetDependencies = (quoteId: any, accountId: any, product: any) => {
  const notifications = useNotifications();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    existingPolicy: null,
    quote: {},
  });

  useEffect(() => {
    setLoading(true);

    if (!accountId || !quoteId) {
      return;
    }

    if (product === ProductTypes.p100) {
      setTimeout(() => {
        Promise.all([
          fetchQuoteDetails(quoteId),
          getPolicyStatus({ quoteId, product }),
        ])
          .then(([quoteResponse, policyResponse]) => {
            setData({
              existingPolicy: _.pick(policyResponse.data, ['id', 'status']),
              quote: {
                ..._.pick(quoteResponse.data, ['id', 'customerStatus']),
                ..._.pick(quoteResponse.data.firmographicData, 'agencyBilled'),
                ..._.pick(
                  quoteResponse.data.initialRequestData,
                  'isEndorsement'
                ),
                ..._.pick(quoteResponse.data, 'totalPremium'),
              },
            });
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
            notifications.error(
              'Something went wrong while trying to load the page, please try again.'
            );
          });
      }, 2000);
    } else if (product === ProductTypes.p250) {
      setTimeout(() => {
        Promise.all([
          fetchP250QuotePageDetails(quoteId),
          getPolicyStatus({ quoteId, product }),
        ])
          .then(([quoteResponse, policyResponse]) => {
            setData({
              existingPolicy: _.pick(policyResponse.data, ['id', 'status']),
              quote: {
                ..._.pick(quoteResponse.data, [
                  'id',
                  'customerStatus',
                  'signUrl',
                ]),
                ..._.pick(quoteResponse.data.firmographicData, 'agencyBilled'),
              },
            });
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
            notifications.error(
              'Something went wrong while trying to load the page, please try again.'
            );
          });
      }, 2000);
    }
  }, [accountId, notifications, product, quoteId]);

  return {
    loading,
    data,
  };
};
